// ============================================================================
// JSPrintManager (JSPM) Singleton Bağlantı Yöneticisi
// ============================================================================
// Kullanım:
//   import { jspm, useJspm } from "@/lib/jspm";
//   const { status, connected, connecting, printers, silentPrintPDF } = useJspm();
//
// Bağlantı sırası (JSPM v9.0, varsayılan port: 29443):
//   1. ws://localhost:29443 probe (self-signed SSL olmadan bağlantı — localhost için tarayıcı izin verir)
//   2. Başarısız → ws://localhost:8181, ws://localhost:8182 ... denenir
//   3. Her port için ws:// başarısız olursa wss:// fallback denenir
//   4. Tüm portlar başarısız → CertificateError veya NotInstalled durumu
//   5. start(secure, "localhost", livePort) ile keşfedilen porta bağlanılır
// ============================================================================

import * as JSPM from "jsprintmanager";
import { useEffect, useState } from "react";

export const JSPM_STATUS_LABEL = {
  0: "Bağlı",
  1: "Kapalı",
  2: "Site politikası tarafından engellendi",
  3: "Kullanıcı yanıtı bekleniyor",
  // v9+ ek durumlari (v6'da bu değerler dönmez)
  4: "JSPM Client yüklü değil",
  5: "SSL sertifika hatası",
  6: "Bağlantı hatası",
};

const DEFAULT_PORTS = [26443, 29443, 8181, 8182, 63318, 54692, 57069]; // 26443=JSPMv6, 29443=JSPMv9
const HANDSHAKE_TIMEOUT_MS = 2500;
const CONNECTING_MAX_MS = 8000;

class JspmManager {
  constructor() {
    this._started = false;
    this._connecting = false;
    this._status = 1; // Closed
    this._printers = [];
    this._listeners = new Set();
    this._activePort = null;
    this._lastError = null;
  }

  // Bir kez çağrılır: uygulama açılışında. Ancak `reinit()` ile port değiştikten sonra tekrar çağrılabilir.
  async init(secure = true, ports = DEFAULT_PORTS, host = "localhost") {
    if (this._started && !this._allowReinit) return;
    this._allowReinit = false;
    this._started = true;
    this._connecting = true;
    this._activePort = null;
    this._lastError = null;
    this._broadcast();

    const primaryScheme = secure ? "wss" : "ws";
    const secondaryScheme = secure ? "ws" : "wss";
    this._host = host || "localhost";
    console.info(`[JSPM] Bağlantı denenecek portlar: ${ports.map((p) => `${primaryScheme}://${this._host}:${p}`).join(", ")} (fallback: ${secondaryScheme}://)`);

    // ─── Port health-check
    let livePort = null;
    let liveScheme = primaryScheme;
    for (const p of ports) {
      console.info(`[JSPM] ${primaryScheme}://localhost:${p} test ediliyor (timeout ${HANDSHAKE_TIMEOUT_MS}ms)...`);
      // eslint-disable-next-line no-await-in-loop
      let res = await this._probePort(primaryScheme, p, this._host);
      if (res.ok) {
        livePort = p; liveScheme = primaryScheme;
        console.info(`[JSPM] ✓ ${primaryScheme}://localhost:${p} yanıt verdi (${res.ms}ms)`);
        break;
      }
      console.warn(`[JSPM] ✗ ${primaryScheme}://localhost:${p} yanıtsız — sebep: ${res.reason}`);
      // Aynı port için alternatif scheme
      console.info(`[JSPM] ${secondaryScheme}://localhost:${p} (fallback scheme) test ediliyor...`);
      // eslint-disable-next-line no-await-in-loop
      res = await this._probePort(secondaryScheme, p, this._host);
      if (res.ok) {
        livePort = p; liveScheme = secondaryScheme;
        console.info(`[JSPM] ✓ ${secondaryScheme}://localhost:${p} yanıt verdi (${res.ms}ms) — scheme otomatik değişti`);
        break;
      }
      console.warn(`[JSPM] ✗ ${secondaryScheme}://localhost:${p} yanıtsız — sebep: ${res.reason}`);
    }

    if (!livePort) {
      const msg = `Hiçbir port erişilebilir değil (denenen: ${ports.join(", ")}). Olası nedenler:
   • JSPrintManager Client uygulaması kurulu değil veya kapalı
   • JSPM v9 varsayılan port 29443 — Client'ın hangi portta çalıştığını Ayarlar'dan kontrol edin
   • İşletim sistemi güvenlik duvarı localhost bağlantısını engelliyor
   • SSL sertifika hatası — bir kez https://localhost:29443 açıp "Bu siteye ilerle" deyin
   • Tarayıcı mixed-content engeli — ws:// denendi ancak bloklandı`;
      console.error(`[JSPM] ${msg}`);
      this._lastError = msg;
      this._connecting = false;
      this._status = 4;   // "Client yüklü değil" — kullanıcıya en anlamlı label
      this._broadcast();
      return;
    }

    this._activePort = livePort;
    this._activeScheme = liveScheme;

    // ─── JSPM library'yi başlat (bulunan scheme + port ile)
    const useSecureNow = liveScheme === "wss";
    try {
      JSPM.JSPrintManager.auto_reconnect = true;
      // start() çağrısı WS nesnesini SENKRON olarak oluşturur
      // onStatusChanged ancak start() SONRASI atanmalı
      console.info(`[JSPM] start() çağrılıyor: secure=${useSecureNow}, host=${this._host}, port=${livePort}`);
      JSPM.JSPrintManager.start(useSecureNow, this._host, livePort).then(
        () => { console.info("[JSPM] start() promise resolved"); this._handleStatusChange(); },
        (err) => {
          console.error("[JSPM] start() reddedildi:", err);
          this._lastError = err?.message || String(err);
          this._handleStatusChange();
        }
      );
      // WS start() içinde senkron oluşturulur — hemen ardından hook kurabiliriz
      const hookWS = () => {
        if (JSPM.JSPrintManager.WS && !JSPM.JSPrintManager.WS._hooked) {
          JSPM.JSPrintManager.WS.onStatusChanged = () => this._handleStatusChange();
          JSPM.JSPrintManager.WS._hooked = true;
          console.info("[JSPM] onStatusChanged hook kuruldu");
        }
      };
      hookWS();                        // Hemen dene (senkron)
      setTimeout(hookWS, 0);          // Sonraki tick
      setTimeout(hookWS, 200);        // Güvenli gecikme
      // Polling fallback: hook çalışmasa bile status izlenir
      this._startStatusPolling();
    } catch (e) {
      console.error("[JSPM] init exception:", e);
      this._lastError = e?.message || String(e);
    }

    // "Bağlanıyor..." göstergesi CONNECTING_MAX_MS sonra kapanır
    setTimeout(() => {
      this._connecting = false;
      this._broadcast();
    }, CONNECTING_MAX_MS);
  }

  async _probePort(scheme, port, host = this._host || "localhost") {
    return new Promise((resolve) => {
      const t0 = performance.now();
      let done = false;
      try {
        const ws = new WebSocket(`${scheme}://${host}:${port}`);
        const timer = setTimeout(() => {
          if (done) return;
          done = true;
          try { ws.close(); } catch (_) {}
          resolve({ ok: false, reason: "timeout" });
        }, HANDSHAKE_TIMEOUT_MS);
        ws.onopen = () => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          try { ws.close(); } catch (_) {}
          resolve({ ok: true, ms: Math.round(performance.now() - t0) });
        };
        ws.onerror = (ev) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve({ ok: false, reason: "error " + (ev?.message || "network") });
        };
      } catch (e) {
        if (done) return;
        done = true;
        resolve({ ok: false, reason: "throw " + (e?.message || String(e)) });
      }
    });
  }

  _handleStatusChange() {
    const s = JSPM.JSPrintManager.websocket_status ?? 1;
    console.info(`[JSPM] WS status değişti → ${s} (${JSPM_STATUS_LABEL[s] || "?"})`);
    this._status = s;
    if (s === JSPM.WSStatus.Open) {
      this._connecting = false;
      this.refreshPrinters();
    } else {
      this._printers = [];
      this._broadcast();
    }
  }

  // Polling fallback: onStatusChanged tetiklenmezse bile status güncellenir
  _startStatusPolling() {
    if (this._pollInterval) clearInterval(this._pollInterval);
    let lastStatus = -1;
    this._pollInterval = setInterval(() => {
      const s = JSPM.JSPrintManager.websocket_status ?? 1;
      if (s !== lastStatus) {
        lastStatus = s;
        this._handleStatusChange();
      }
    }, 1000);
  }

  async refreshPrinters() {
    try {
      const names = await JSPM.JSPrintManager.getPrinters(true);
      this._printers = Array.isArray(names) ? names : [];
      console.info(`[JSPM] ${this._printers.length} yazıcı bulundu`);
    } catch (e) {
      console.error("[JSPM] getPrinters hatası:", e);
      this._printers = [];
    }
    this._broadcast();
  }

  _snapshot() {
    return {
      status: this._status,
      connected: this._status === 0,   // JSPM.WSStatus.Open
      connecting: this._connecting,
      statusLabel: this._connecting ? "Bağlanıyor..." : (JSPM_STATUS_LABEL[this._status] || "Bilinmiyor"),
      printers: [...this._printers],
      activePort: this._activePort,
      activeScheme: this._activeScheme || (this._status === 0 ? "ws" : null),
      lastError: this._lastError,
    };
  }

  _broadcast() {
    const snap = this._snapshot();
    this._listeners.forEach((cb) => { try { cb(snap); } catch (_) {} });
  }

  subscribe(cb) {
    this._listeners.add(cb);
    cb(this._snapshot());
    return () => this._listeners.delete(cb);
  }

  isConnected() { return this._status === 0; }

  // Kullanıcı tarafından çağrılabilir port keşif tester'ı
  probePort(scheme, port, host) {
    return this._probePort(scheme, port, host);
  }

  // Kullanıcı Ayarlar'dan port değiştirdiğinde çağrılır
  async reinit(secure = true, ports = DEFAULT_PORTS, host = this._host || "localhost") {
    console.info("[JSPM] Yeniden bağlanma başlatılıyor...");
    this._allowReinit = true;
    // WS'in mevcut bağlantısını kapat
    try {
      if (JSPM.JSPrintManager.WS && typeof JSPM.JSPrintManager.WS.close === "function") {
        JSPM.JSPrintManager.WS.close();
      }
    } catch (_) {}
    return this.init(secure, ports, host);
  }

  async silentPrintPDF(printerName, blobOrUrl, filename = "document.pdf", copies = 1) {
    if (!this.isConnected()) throw new Error("JSPM bağlı değil");
    if (!printerName) throw new Error("Yazıcı seçilmedi");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.InstalledPrinter(printerName);
    let file;
    if (typeof blobOrUrl === "string") {
      file = new JSPM.PrintFilePDF(blobOrUrl, JSPM.FileSourceType.URL, filename, copies);
    } else if (blobOrUrl instanceof Blob) {
      file = new JSPM.PrintFilePDF(blobOrUrl, JSPM.FileSourceType.BLOB, filename, copies);
    } else {
      throw new Error("PDF kaynağı Blob veya URL olmalı");
    }
    cpj.files.push(file);
    await cpj.sendToClient();
    return { ok: true };
  }

  async silentPrintRaw(printerName, commands) {
    if (!this.isConnected()) throw new Error("JSPM bağlı değil");
    if (!printerName) throw new Error("Yazıcı seçilmedi");
    const cpj = new JSPM.ClientPrintJob();
    cpj.clientPrinter = new JSPM.InstalledPrinter(printerName);
    if (typeof commands === "string") {
      cpj.printerCommands = commands;
    } else if (commands instanceof Uint8Array) {
      cpj.binaryPrinterCommands = commands;
    } else {
      throw new Error("Komut string veya Uint8Array olmalı");
    }
    await cpj.sendToClient();
    return { ok: true };
  }

  async silentPrintZPL(printerName, zpl) {
    if (!zpl || typeof zpl !== "string") throw new Error("Geçersiz ZPL komutu");
    return this.silentPrintRaw(printerName, zpl);
  }

  async testPrint(printerName) {
    const commands =
      "\x1B@\x1B!\x08ZEN ERP TEST ETIKETI\x0A\x1B!\x00" +
      "Yazici: " + printerName + "\x0A" +
      "Baglantı: JSPM OK\x0A" +
      "Tarih: " + new Date().toLocaleString("tr-TR") + "\x0A\x0A" +
      "Test basarili!\x0A\x0A\x0A";
    return this.silentPrintRaw(printerName, commands);
  }
}

export const jspm = new JspmManager();

// React hook
export function useJspm() {
  const [state, setState] = useState({
    status: 1,
    connected: false,
    connecting: false,
    statusLabel: "Kapalı",
    printers: [],
    activePort: null,
    lastError: null,
  });
  useEffect(() => {
    const unsub = jspm.subscribe(setState);
    return unsub;
  }, []);
  return state;
}
